import * as t from "io-ts";
import { UnitOptionsV } from "@/types/unit";
import { inputTextValidationRules } from "@/util/input-text-validation-rules";
import { IconName, iconNames } from "@/lib/icons/icons";
import { getIoTsStringsUnionFromConstStringArray } from "@/util/type-assertions";
import { MediaOptionsV, MediaSizeOptionsV } from "@/types/media";
import {
  DynamicPropertyPath,
  DynamicPropertyPathV
} from "@/types/dynamic-property-path";
import { ColorOptionsV, ColorPickerTypeV } from "@/types/colors";

export const ControlTypeV = t.union([
  t.literal("section"),
  t.literal("text"),
  t.literal("url"),
  t.literal("richtext"),
  t.literal("number"),
  t.literal("dropdown"),
  t.literal("multiselect"),
  t.literal("button_bar"),
  t.literal("toggle"),
  t.literal("unit"),
  t.literal("color"),
  t.literal("wpmedia"),
  t.literal("code"),
  t.literal("repeater"),
  t.literal("inline_repeater"),
  t.literal("condition"),
  t.literal("font_family"),
  t.literal("spacing_complex"),
  t.literal("border_complex"),
  t.literal("border_radius"),
  t.literal("class"),
  t.literal("global_colors"),
  t.literal("global_block_chooser"),
  t.literal("icon"),
  t.literal("breakpoint_dropdown"),
  t.literal("media_size_dropdown"),
  t.literal("gradient"),
  t.literal("shadow"),
  t.literal("message"),
  t.literal("focus_point"),
  t.literal("typography"),
  t.literal("custom_typography"),
  t.literal("typography_preset"),
  t.literal("element_screenshot"),
  t.literal("video"),
  t.literal("post_chooser"),
  t.literal("link"),
  t.literal("slider"),
  t.literal("wp_query"),
  t.literal("add_registered_children"),
  t.literal("trigger_action_button"),
  t.literal("builder_behavior"),
  t.literal("alert_box"),
  t.literal("pro_only_alert_box"),
  t.literal("date_picker"),
  t.literal("time_picker"),
  t.literal("api_key_input"),
  t.literal("conditional_form_field")
]);

export const CONTROL_TYPES: ControlType[] = ControlTypeV.types.map(
  controlType => controlType.value
);

export type ControlType = t.TypeOf<typeof ControlTypeV>;

export const DatePickerOptionsV = t.partial({
  range: t.boolean,
  multiple: t.boolean
});

export type DatePickerOptions = t.TypeOf<typeof DatePickerOptionsV>;

export const ButtonBarOptionsV = t.partial({
  size: t.union([t.literal("big"), t.literal("small")]),
  layout: t.union([t.literal("multiline"), t.literal("default")]),
  mandatory: t.boolean,
  multiple: t.boolean
});

export type ButtonBarOptions = t.TypeOf<typeof ButtonBarOptionsV>;
export type ButtonBarItem = {
  text: string;
  value: string;
  icon?: IconName;
  svg?: string;
};

const ApiKeyOptionsV = t.type({
  apiKeyName: t.string,
  urlKeyName: t.string,
  apiKeyLabel: t.union([t.string, t.undefined]),
  urlKeyLabel: t.union([t.string, t.undefined])
});

export type ApiKeyOptions = t.TypeOf<typeof ApiKeyOptionsV>;

const UiIconV = t.union([
  getIoTsStringsUnionFromConstStringArray(iconNames),
  t.string
]);

export const ButtonBarOrDropdownItemV = t.intersection([
  t.type({
    text: t.string,
    value: t.unknown
  }),
  t.partial({
    icon: UiIconV
  })
]);

export const ControlConditionOperandV = t.union([
  t.literal("equals"),
  t.literal("not equals"),
  t.literal("is one of"),
  t.literal("is none of"),
  t.literal("is set"),
  t.literal("is not set"),
  t.literal("includes"),
  t.literal("contains"),
  t.literal("does not contain"),
  t.undefined
]);

export type ControlConditionOperand = t.TypeOf<typeof ControlConditionOperandV>;

const ControlConditionSingleV = t.intersection([
  t.type({
    path: t.string,
    operand: ControlConditionOperandV,
    value: t.unknown
  }),
  t.partial({
    external: t.boolean
  })
]);

export type ControlConditionSingle = t.TypeOf<typeof ControlConditionSingleV>;

export const ControlConditionsV = t.union([
  // support backwards compatibility
  ControlConditionSingleV,
  // all conditions together are AND condition. Each array is OR
  t.array(t.array(ControlConditionSingleV))
]);

export type ControlConditions = t.TypeOf<typeof ControlConditionsV>;

export type ButtonBarOrDropdownItem = t.TypeOf<typeof ButtonBarOrDropdownItemV>;

export const RepeaterOptionsV = t.intersection([
  t.type({
    titleTemplate: t.string
  }),
  t.partial({
    buttonName: t.string,
    defaultTitle: t.string,
    defaultNewValue: t.unknown,
    duplicateNewValue: t.unknown,
    noDuplicate: t.boolean,
    galleryMode: t.boolean,
    galleryMediaPath: t.string,
    deleteConfirm: t.boolean,
    deleteText: t.string,
    forceDontProcessDefaultNewValueWhenAddingItemsHack: t.boolean
  })
]);

export type RepeaterOptions = t.TypeOf<typeof RepeaterOptionsV>;

export const RangeOptionsV = t.intersection([
  t.type({
    min: t.union([t.number, t.string, t.undefined]),
    max: t.union([t.number, t.string, t.undefined]),
    step: t.union([t.number, t.string, t.undefined])
  }),
  t.partial({
    disableSlider: t.boolean,
    labels: t.array(t.string)
  })
]);

export type RangeOptions = t.TypeOf<typeof RangeOptionsV>;

export const ShadowOptionsV = t.type({
  type: t.union([t.literal("text"), t.literal("box")])
});

export type ShadowOptions = t.TypeOf<typeof ShadowOptionsV>;

const PopulateWithFetchV = t.partial({
  fetchDataAction: t.string,
  fetchContextPath: t.string,
  refetchPaths: t.array(t.string)
});

export type PopulateWithFetch = t.TypeOf<typeof PopulateWithFetchV>;

const MultiselectOptionsV = t.partial({
  populate: PopulateWithFetchV,
  makeCombobox: t.boolean,
  searchable: t.boolean
});

export type MultiselectOptions = t.TypeOf<typeof MultiselectOptionsV>;

export const PopulateOptionsV = t.partial({
  path: t.string,
  text: t.string,
  value: t.string,
  condition: ControlConditionsV
});
export type PopulateOptions = t.TypeOf<typeof PopulateOptionsV>;

export const DropdownOptionsV = t.partial({
  populate: t.intersection([PopulateOptionsV, PopulateWithFetchV]),
  noDataAvailableMessage: t.string
});

export type DropdownOptions = t.TypeOf<typeof DropdownOptionsV>;

export const BreakpointOptionsV = t.partial({
  multiple: t.boolean,
  enableNever: t.boolean
});

export type BreakpointOptions = t.TypeOf<typeof BreakpointOptionsV>;

export const textAndRichTextVariableItemV = t.type({
  text: t.string,
  value: t.unknown
});
export type textAndRichTextVariableItem = t.TypeOf<
  typeof textAndRichTextVariableItemV
>;

export const textAndRichTextVariableOptionsV = t.partial({
  enabled: t.boolean,
  populate: PopulateOptionsV
});
export type textAndRichTextVariableOptions = t.TypeOf<
  typeof textAndRichTextVariableOptionsV
>;

export const FocusPointOptionsV = t.partial({
  imagePropertyPath: t.string,
  gridMode: t.boolean
});

export type FocusPointOptions = t.TypeOf<typeof FocusPointOptionsV>;

// Alert Box
export const AlertBoxStyleV = t.union([
  t.literal("default"),
  t.literal("info"),
  t.literal("success"),
  t.literal("error"),
  t.literal("warning")
]);

export const AlertBoxOptionsV = t.partial({
  style: AlertBoxStyleV,
  content: t.string
});

export type AlertBoxStyle = t.TypeOf<typeof AlertBoxStyleV>;
export type AlertBoxOptions = t.TypeOf<typeof AlertBoxOptionsV>;

// Video

export const OEmbedProviderV = t.union([
  t.literal("youtube"),
  t.literal("vimeo"),
  t.literal("dailymotion"),
  t.literal("flickr"),
  t.literal("scribd"),
  t.literal("wordpress.tv"),
  t.literal("crowdsignal"),
  t.literal("smugmug"),
  t.literal("twitter"),
  t.literal("slideshare"),
  t.literal("soundcloud"),
  t.literal("spotify"),
  t.literal("imgur"),
  t.literal("meetup.com"),
  t.literal("animoto"),
  t.literal("issuu"),
  t.literal("mixcloud"),
  t.literal("ted"),
  t.literal("tumblr"),
  t.literal("kickstarter"),
  t.literal("videopress")
]);

export const OEmbedV = t.intersection([
  t.type({
    url: t.string,
    embedUrl: t.string,
    format: t.union([
      t.literal("photo"),
      t.literal("video"),
      t.literal("rich"),
      t.literal("link")
    ]),
    type: t.union([t.literal("oembed"), t.literal("video")]),
    source: t.union([OEmbedProviderV, t.literal("local"), t.undefined])
  }),
  t.partial({
    title: t.string,
    thumbnail: t.string,
    mime: t.string,
    provider: OEmbedProviderV,
    videoId: t.string
  })
]);

export type VideoOptions = t.TypeOf<typeof VideoOptionsV>;

export const VideoOptionsV = t.partial({
  providers: t.array(OEmbedProviderV)
});

export type OEmbed = t.TypeOf<typeof OEmbedV>;
export type OEmbedProvider = t.TypeOf<typeof OEmbedProviderV>;

// Icons

export const IconOptionsV = t.partial({
  suggestions: t.array(t.string)
});

export type IconOptions = t.TypeOf<typeof IconOptionsV>;

export const CustomTypographyOptionsV = t.partial({
  hoverableEverything: t.boolean,
  enableMediaQueries: t.boolean
});

export type CustomTypographyOptions = t.TypeOf<typeof CustomTypographyOptionsV>;

export const TypographyOptionsV = t.partial({
  customTypographyOptions: CustomTypographyOptionsV
});

export type TypographyOptions = t.TypeOf<typeof TypographyOptionsV>;

const layoutOptions = t.union([
  t.literal("inline"),
  t.literal("vertical"),
  t.literal("vertical50")
  // TODO - 'vertical50' is actually unsupported as a section layout.
  // It should be removed from all elements.
  // The only reason it's still here is to stop io-ts from blowing up
  // for our design team that may have some in development elements that still use
  // layout = 'vertical50'
]);

const CodeOptionsLanguagesV = t.union([
  t.literal("css"),
  t.literal("html"),
  t.literal("javascript"),
  t.literal("x-php")
]);

export const CodeOptionsV = t.intersection([
  t.type({
    language: CodeOptionsLanguagesV
  }),
  t.partial({
    autofillOnEmpty: t.string
  })
]);

export type CodeOptions = t.TypeOf<typeof CodeOptionsV>;

const AddRegisteredChildrenOptionsV = t.type({
  addExtraElementsBySlug: t.array(t.string)
});

export type AddRegisteredChildrenOptions = t.TypeOf<
  typeof AddRegisteredChildrenOptionsV
>;

export const controlLayoutTypes = ["accordion", "popout", "modal"] as const;

export const LayoutTypeV =
  getIoTsStringsUnionFromConstStringArray(controlLayoutTypes);

export type LayoutType = t.TypeOf<typeof LayoutTypeV>;

export const ControlOptionsV = t.intersection([
  t.type({
    type: ControlTypeV
  }),
  t.partial({
    noLabel: t.boolean,
    layout: layoutOptions,
    autofocus: t.boolean,
    codeOptions: CodeOptionsV,
    buttonBarOptions: ButtonBarOptionsV,
    items: t.array(ButtonBarOrDropdownItemV),
    repeaterOptions: RepeaterOptionsV,
    unitOptions: UnitOptionsV,
    rangeOptions: RangeOptionsV,
    shadowOptions: ShadowOptionsV,
    alertBoxOptions: AlertBoxOptionsV,
    sectionOptions: t.partial({
      preset: t.partial({
        slug: t.string,
        codeHelp: t.string,
        relativePropertyPathsToWhitelistInFlatProps: t.array(t.string),
        relativeDynamicPropertyPaths: t.array(DynamicPropertyPathV)
      }),
      type: LayoutTypeV
    }),
    dropdownOptions: DropdownOptionsV,
    breakpointOptions: BreakpointOptionsV,
    condition: ControlConditionsV,
    hideForElements: t.array(t.string),
    // inputOptions: t.unknown, // can be any of HTMLInputElement,
    messageOptions: t.type({ text: t.string }),
    isExternal: t.boolean,
    placeholder: t.string,
    textOptions: t.partial({
      multiline: t.boolean,
      validationFunctionName: t.keyof(inputTextValidationRules),
      format: t.union([t.literal("plain"), t.literal("html")])
    }),
    linkOptions: t.partial({
      hideAdvanced: t.boolean
    }),
    colorOptions: ColorOptionsV,
    globalColorOptions: t.partial({
      type: ColorPickerTypeV
    }),
    mediaOptions: MediaOptionsV,
    focusPointOptions: FocusPointOptionsV,
    mediaSizeOptions: MediaSizeOptionsV,
    iconOptions: IconOptionsV,
    customTypographyOptions: CustomTypographyOptionsV,
    typographyOptions: TypographyOptionsV,
    ApiKeyOptions: ApiKeyOptionsV,
    triggerActionsButtonOptions: t.partial({
      text: t.string,
      icon: UiIconV
    }),
    multiselectOptions: MultiselectOptionsV,
    datePickerOptions: DatePickerOptionsV,
    addRegisteredChildrenOptions: AddRegisteredChildrenOptionsV,
    variableOptions: textAndRichTextVariableOptionsV,
    variableItems: t.array(textAndRichTextVariableItemV)
  })
]);

export type ControlOptions = t.TypeOf<typeof ControlOptionsV>;

export const ControlV: t.Type<Control> = t.recursion("Control", () =>
  t.exact(
    t.type({
      slug: t.string,
      label: t.string,
      keywords: t.union([t.array(t.string), t.undefined]),
      options: ControlOptionsV,
      enableMediaQueries: t.boolean,
      enableHover: t.boolean,
      children: t.array(ControlV)
    })
  )
);

// We don't need the preset to have *all* section options when saving it to an element
// since only these can be edited when added to an element
export type GetPresetSectionOptions = {
  condition?: ControlConditions;
  type?: LayoutType;
};

export type Control = {
  slug: string;
  label: string;
  keywords: string[] | undefined;
  options: ControlOptions;
  enableMediaQueries: boolean;
  enableHover: boolean;
  children: Array<Control>;
};

export type ControlWithinTree = {
  control: Control;
  controlsTree: Control[];
};

export type ChangeControlValueMeta = {
  disableTransactionBundling?: boolean;
  nestedControlThatWasChanged?: NestedControlThatWasChanged;
};

export type NestedControlThatWasChanged = ControlWithinTree & {
  path: string;
  meta?: ChangeControlValueMeta;
};

export function decorateValueEmittedByInputWithMetadata<T>(
  value: T,
  metadata: ChangeControlValueMeta
): ValueEmittedFromControlInputDecoratedWithMetadata<T> {
  return new ValueEmittedFromControlInputDecoratedWithMetadata<T>(
    value,
    metadata
  );
}

export class ValueEmittedFromControlInputDecoratedWithMetadata<
  T extends unknown
> {
  constructor(
    public readonly value: T,
    public readonly meta: ChangeControlValueMeta
  ) {}
}

export interface ControlsTreeContext {
  getControlValue: (controlPath: string) => unknown;
  setControlValue: (
    controlPathProbablyWithBreakpointIdAndHoverSuffix: string,
    newValue: unknown,
    control: Control,
    meta?: ChangeControlValueMeta
  ) => void;
  getControls:
    | ((controlPath: string) => Control[] | undefined)
    | (() => Control[] | undefined);
  dynamicProperty: (controlPath: string) => false | DynamicPropertyPath;
  setAccordionState: (accordionPath: string, open: boolean) => void;
  getAccordionState: (accordionPath: string) => boolean | undefined;
}
